import React, { useState, useEffect } from 'react';
import en from '../../../translations/en.json';
import { apiClient } from '../../../api/apiClient';
import { GET_ALBUM_ARTISTS_URL } from '../../../api/album';
import EmptyState from '../../../components/shared/EmptyState';
import Loader from '../../../components/shared/Loader';

function ArtistAvatarItem({ src, name }) {
  const [imgError, setImgError] = useState(false);
  const initial = name ? name.trim().charAt(0).toUpperCase() : 'A';

  if (src && !imgError) {
    return (
      <img 
        src={src} 
        alt={name} 
        className="artist-card-img" 
        onError={() => setImgError(true)} 
      />
    );
  }

  return (
    <div 
      className="artist-card-img" 
      style={{
        background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
        color: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '24px',
        fontWeight: '700',
        textTransform: 'uppercase',
        userSelect: 'none'
      }}
    >
      {initial}
    </div>
  );
}

function ArtistsTab({ album, onSelectArtist }) {
  const [summary, setSummary] = useState({ singers: 0, lyricists: 0, musicians: 0 });
  const [artists, setArtists] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const t = (key) => {
    return key.split('.').reduce((obj, i) => (obj ? obj[i] : null), en) || key;
  };

  useEffect(() => {
    let isMounted = true;
    if (album && album.id) {
      setIsLoading(true);
      const fetchAlbumArtists = async () => {
        try {
          const res = await apiClient(GET_ALBUM_ARTISTS_URL(album.id));
          if (res && isMounted) {
            setSummary(res.summary || { singers: 0, lyricists: 0, musicians: 0 });
            setArtists(res.artists || []);
          }
        } catch (err) {
          console.error('Failed to fetch album artists:', err);
        } finally {
          if (isMounted) setIsLoading(false);
        }
      };
      fetchAlbumArtists();
    }
    return () => {
      isMounted = false;
    };
  }, [album?.id]);

  const formatCount = (num) => String(num || 0).padStart(2, '0');

  return (
    <div className="artists-tab-container">
      {/* Summary Metric Cards at the Top */}
      <div className="artists-metrics-grid">
        {/* Metric 1: Singers */}
        <div className="artist-metric-card">
          <div className="metric-icon-wrapper circle-blue">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </div>
          <div className="metric-details">
            <span className="metric-number">{formatCount(summary.singers)}</span>
            <span className="metric-label">{t('albumDetails.artistsSinger')}</span>
          </div>
        </div>

        {/* Metric 2: Lyricists */}
        <div className="artist-metric-card">
          <div className="metric-icon-wrapper circle-orange">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M12 20h9" />
              <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
            </svg>
          </div>
          <div className="metric-details">
            <span className="metric-number">{formatCount(summary.lyricists)}</span>
            <span className="metric-label">{t('albumDetails.artistsLyricsWriters')}</span>
          </div>
        </div>

        {/* Metric 3: Musicians */}
        <div className="artist-metric-card">
          <div className="metric-icon-wrapper circle-green">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </div>
          <div className="metric-details">
            <span className="metric-number">{formatCount(summary.musicians)}</span>
            <span className="metric-label">{t('albumDetails.artistsMusicians')}</span>
          </div>
        </div>
      </div>

      {/* Grid of Artist Cards */}
      {isLoading ? (
        <Loader label="Fetching artists, please wait." />
      ) : artists.length === 0 ? (
        <EmptyState title="No Artists Found" subtitle="This album has no artists associated yet." />
      ) : (
        <div className="artists-cards-grid">
          {artists.map((artist) => (
            <div 
              key={artist.id} 
              className="artist-profile-card"
              onClick={() => onSelectArtist && onSelectArtist(artist)}
              style={{ cursor: 'pointer' }}
            >
              <div className="artist-img-wrapper">
                <ArtistAvatarItem src={artist.imageUrl || artist.image_url || artist.avatar} name={artist.name} />
              </div>
              
              <div className="artist-card-body">
                <h4 className="artist-card-name">{artist.name}</h4>
                <div className="artist-role-row" style={{ flexWrap: 'wrap', gap: '6px' }}>
                  {artist.roles && artist.roles.map((role) => (
                    <span 
                      key={role} 
                      className={`artist-role-badge role-${role.toLowerCase()}`}
                    >
                      {role}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ArtistsTab;
